<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from '../../../../components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PipelineFollowUpRulesAPI from 'dashboard/api/pipelineFollowUpRules';
import SendWindowFields from 'dashboard/components-next/follow-ups/SendWindowFields.vue';
import SendWindowBadge from 'dashboard/components-next/follow-ups/SendWindowBadge.vue';
import { isSendWindowValid } from 'dashboard/composables/useSendWindow';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  pipeline: {
    type: Object,
    required: false,
    default: null,
  },
});

const emit = defineEmits(['close']);
const store = useStore();
const route = useRoute();
const { t } = useI18n();
const { showAlert } = useAlert();

const isLoading = ref(true);
const isSaving = ref(false);
const rules = ref([]);
const showForm = ref(false);
const editingRule = ref(null);

const form = ref({
  follow_up_template_id: null,
  sender_id: null,
  from_stage: '',
  to_stage: '',
  delay_minutes: 0,
  delay_unit: 'minutes',
  enabled: true,
  send_window: {},
});

// Get stages from pipeline
const stages = computed(() => {
  if (!props.pipeline?.stages) return [];
  return Object.entries(props.pipeline.stages).map(([key, value]) => ({
    key,
    name: value.name || key,
  }));
});

// Get templates from store
const templates = computed(
  () => store.getters['followUpTemplates/getTemplates'] || []
);

// Agents (account-scoped by the /agents endpoint) for the optional sender field.
const agents = computed(() => store.getters['agents/getAgents'] || []);

const fetchRules = async () => {
  if (!props.pipeline?.id) return;

  isLoading.value = true;
  try {
    const response = await PipelineFollowUpRulesAPI.get(props.pipeline.id);
    rules.value = response.data.payload || [];
  } catch {
    showAlert(t('FOLLOW_UP_RULES.MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const fetchTemplates = async () => {
  try {
    await store.dispatch('followUpTemplates/fetch');
  } catch {
    // Templates may already be loaded
  }
};

const fetchAgents = async () => {
  try {
    await store.dispatch('agents/get');
  } catch {
    // Agents may already be loaded
  }
};

// Fetch rules when modal opens
watch(
  () => props.show,
  async newVal => {
    if (newVal) {
      await fetchRules();
      await fetchTemplates();
      await fetchAgents();
    }
  }
);

onMounted(async () => {
  if (props.show) {
    await fetchRules();
    await fetchTemplates();
    await fetchAgents();
  }
});

const resetForm = () => {
  form.value = {
    follow_up_template_id: templates.value[0]?.id || null,
    sender_id: null,
    from_stage: '',
    to_stage: stages.value[0]?.key || '',
    delay_minutes: 0,
    delay_unit: 'minutes',
    enabled: true,
    send_window: {},
  };
};

const handleClose = () => {
  showForm.value = false;
  editingRule.value = null;
  resetForm();
  emit('close');
};

const openCreateForm = () => {
  editingRule.value = null;
  resetForm();
  showForm.value = true;
};

const openEditForm = rule => {
  editingRule.value = rule;

  // Parse delay into value and unit
  let delayValue = rule.delay_minutes;
  let delayUnit = 'minutes';

  if (delayValue >= 1440 && delayValue % 1440 === 0) {
    delayValue /= 1440;
    delayUnit = 'days';
  } else if (delayValue >= 60 && delayValue % 60 === 0) {
    delayValue /= 60;
    delayUnit = 'hours';
  }

  form.value = {
    follow_up_template_id: rule.follow_up_template_id,
    sender_id: rule.sender_id || null,
    from_stage: rule.from_stage || '',
    to_stage: rule.to_stage,
    delay_minutes: delayValue,
    delay_unit: delayUnit,
    enabled: rule.enabled,
    send_window: { ...(rule.send_window || {}) },
  };
  showForm.value = true;
};

const calculateDelayMinutes = () => {
  const value = parseInt(form.value.delay_minutes, 10) || 0;
  switch (form.value.delay_unit) {
    case 'hours':
      return value * 60;
    case 'days':
      return value * 1440;
    default:
      return value;
  }
};

const handleSubmit = async () => {
  if (!form.value.to_stage || !form.value.follow_up_template_id) {
    showAlert(t('FOLLOW_UP_RULES.MESSAGES.VALIDATION_ERROR'));
    return;
  }

  if (!isSendWindowValid(form.value.send_window)) {
    showAlert(t('FOLLOW_UP_SEND_WINDOW.INVALID'));
    return;
  }

  isSaving.value = true;

  const data = {
    follow_up_template_id: form.value.follow_up_template_id,
    sender_id: form.value.sender_id || null,
    from_stage: form.value.from_stage || null,
    to_stage: form.value.to_stage,
    delay_minutes: calculateDelayMinutes(),
    enabled: form.value.enabled,
    send_window: form.value.send_window,
  };

  try {
    if (editingRule.value) {
      await PipelineFollowUpRulesAPI.update(
        props.pipeline.id,
        editingRule.value.id,
        data
      );
      showAlert(t('FOLLOW_UP_RULES.MESSAGES.UPDATE_SUCCESS'));
    } else {
      await PipelineFollowUpRulesAPI.create(props.pipeline.id, data);
      showAlert(t('FOLLOW_UP_RULES.MESSAGES.CREATE_SUCCESS'));
    }

    showForm.value = false;
    editingRule.value = null;
    resetForm();
    await fetchRules();
  } catch {
    showAlert(t('FOLLOW_UP_RULES.MESSAGES.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleDelete = async rule => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('FOLLOW_UP_RULES.MESSAGES.DELETE_CONFIRM'))) return;

  try {
    await PipelineFollowUpRulesAPI.delete(props.pipeline.id, rule.id);
    showAlert(t('FOLLOW_UP_RULES.MESSAGES.DELETE_SUCCESS'));
    await fetchRules();
  } catch {
    showAlert(t('FOLLOW_UP_RULES.MESSAGES.ERROR'));
  }
};

const toggleEnabled = async rule => {
  try {
    await PipelineFollowUpRulesAPI.update(props.pipeline.id, rule.id, {
      enabled: !rule.enabled,
    });
    await fetchRules();
  } catch {
    showAlert(t('FOLLOW_UP_RULES.MESSAGES.ERROR'));
  }
};

const getStageName = stageKey => {
  const stage = stages.value.find(s => s.key === stageKey);
  return stage?.name || stageKey;
};

const getDelayDescription = rule => {
  const minutes = rule.delay_minutes || 0;
  if (minutes === 0) return t('FOLLOW_UP_RULES.FORM.DELAY_HINT');

  if (minutes < 60) {
    return `${minutes} ${t('FOLLOW_UP_RULES.FORM.MINUTES').toLowerCase()}`;
  }
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    return `${hours} ${t('FOLLOW_UP_RULES.FORM.HOURS').toLowerCase()}`;
  }
  const days = Math.floor(minutes / 1440);
  return `${days} ${t('FOLLOW_UP_RULES.FORM.DAYS').toLowerCase()}`;
};
</script>

<template>
  <Modal :show="show" size="large" @close="handleClose">
    <div class="flex flex-col max-h-[80vh]">
      <!-- Header -->
      <header class="flex-shrink-0 px-4 pt-4 pb-3">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('FOLLOW_UP_RULES.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-0.5">
          {{ t('FOLLOW_UP_RULES.DESCRIPTION') }}
        </p>
      </header>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4">
        <!-- Loading -->
        <div v-if="isLoading" class="flex items-center justify-center py-8">
          <span class="i-svg-spinners-90-ring-with-bg size-6 text-n-brand" />
        </div>

        <!-- Form -->
        <div v-else-if="showForm" class="space-y-4">
          <div class="p-4 bg-n-alpha-2 rounded-lg border border-n-weak">
            <h4 class="text-sm font-medium text-n-slate-12 mb-4">
              {{
                editingRule
                  ? t('FOLLOW_UP_RULES.FORM.EDIT_TITLE')
                  : t('FOLLOW_UP_RULES.FORM.CREATE_TITLE')
              }}
            </h4>

            <!-- Template Selection -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                {{ t('FOLLOW_UP_RULES.FORM.TEMPLATE') }}
              </label>
              <div
                v-if="templates.length === 0"
                class="p-3 border border-n-amber rounded-lg bg-n-amber-3"
              >
                <p class="text-sm text-n-amber-11">
                  {{ t('FOLLOW_UP_RULES.FORM.NO_TEMPLATES') }}
                </p>
                <a
                  :href="`/app/accounts/${route.params.accountId}/follow-ups/templates`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm font-medium text-n-brand hover:underline mt-1 inline-block"
                >
                  {{ t('FOLLOW_UP_RULES.FORM.CREATE_TEMPLATE') }}
                </a>
              </div>
              <div v-else class="relative">
                <select
                  v-model="form.follow_up_template_id"
                  class="w-full h-10 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-alpha-1 text-n-slate-12 cursor-pointer"
                  required
                >
                  <option :value="null" disabled>
                    {{ t('FOLLOW_UP_RULES.FORM.SELECT_TEMPLATE') }}
                  </option>
                  <option
                    v-for="template in templates"
                    :key="template.id"
                    :value="template.id"
                  >
                    {{ template.name }}
                  </option>
                </select>
                <span
                  class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                >
                  <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
                </span>
              </div>
            </div>

            <!-- Sender (optional agent that authors the follow-up message) -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                {{ t('FOLLOW_UP_RULES.FORM.SENDER.LABEL') }}
              </label>
              <div class="relative">
                <select
                  v-model="form.sender_id"
                  class="w-full h-10 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-alpha-1 text-n-slate-12 cursor-pointer"
                >
                  <option :value="null">
                    {{ t('FOLLOW_UP_RULES.FORM.SENDER.DEFAULT') }}
                  </option>
                  <option
                    v-for="agent in agents"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
                <span
                  class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                >
                  <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
                </span>
              </div>
              <p class="mt-1.5 text-xs text-n-slate-10">
                {{ t('FOLLOW_UP_RULES.FORM.SENDER.HINT') }}
              </p>
            </div>

            <!-- Stage Transition -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('FOLLOW_UP_RULES.FORM.FROM_STAGE') }}
                </label>
                <div class="relative">
                  <select
                    v-model="form.from_stage"
                    class="w-full h-10 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-alpha-1 text-n-slate-12 cursor-pointer"
                  >
                    <option value="">
                      {{ t('FOLLOW_UP_RULES.FORM.ANY_STAGE') }}
                    </option>
                    <option
                      v-for="stage in stages"
                      :key="stage.key"
                      :value="stage.key"
                    >
                      {{ stage.name }}
                    </option>
                  </select>
                  <span
                    class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  >
                    <span
                      class="i-lucide-chevron-down size-4 text-n-slate-10"
                    />
                  </span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                  {{ t('FOLLOW_UP_RULES.FORM.TO_STAGE') }} *
                </label>
                <div class="relative">
                  <select
                    v-model="form.to_stage"
                    class="w-full h-10 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-alpha-1 text-n-slate-12 cursor-pointer"
                    required
                  >
                    <option
                      v-for="stage in stages"
                      :key="stage.key"
                      :value="stage.key"
                    >
                      {{ stage.name }}
                    </option>
                  </select>
                  <span
                    class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  >
                    <span
                      class="i-lucide-chevron-down size-4 text-n-slate-10"
                    />
                  </span>
                </div>
              </div>
            </div>

            <!-- Delay -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-n-slate-11 mb-1.5">
                {{ t('FOLLOW_UP_RULES.FORM.DELAY') }}
              </label>
              <div class="flex items-center gap-3">
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                <input
                  v-model.number="form.delay_minutes"
                  type="number"
                  min="0"
                  :placeholder="t('FOLLOW_UP_RULES.FORM.DELAY_HINT')"
                  class="w-24 h-10 px-3 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-alpha-1 text-n-slate-12 text-center"
                />
                <div class="relative flex-1">
                  <select
                    v-model="form.delay_unit"
                    class="w-full h-10 pl-3 pr-10 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-alpha-1 text-n-slate-12 cursor-pointer"
                  >
                    <option value="minutes">
                      {{ t('FOLLOW_UP_RULES.FORM.MINUTES') }}
                    </option>
                    <option value="hours">
                      {{ t('FOLLOW_UP_RULES.FORM.HOURS') }}
                    </option>
                    <option value="days">
                      {{ t('FOLLOW_UP_RULES.FORM.DAYS') }}
                    </option>
                  </select>
                  <span
                    class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  >
                    <span
                      class="i-lucide-chevron-down size-4 text-n-slate-10"
                    />
                  </span>
                </div>
              </div>
              <p class="mt-1.5 text-xs text-n-slate-10">
                {{ t('FOLLOW_UP_RULES.FORM.DELAY_HINT') }}
              </p>
            </div>

            <!-- Send Window -->
            <div class="mb-4">
              <SendWindowFields v-model="form.send_window" />
            </div>

            <!-- Enabled -->
            <div class="mb-4">
              <label class="flex items-center gap-3 cursor-pointer">
                <input
                  v-model="form.enabled"
                  type="checkbox"
                  class="w-5 h-5 rounded border-n-weak text-n-brand focus:ring-n-brand focus:ring-offset-0"
                />
                <span class="text-sm font-medium text-n-slate-11">
                  {{ t('FOLLOW_UP_RULES.FORM.ENABLED') }}
                </span>
              </label>
            </div>

            <!-- Important Notice -->
            <div class="p-3 bg-n-amber-3 border border-n-amber rounded-lg">
              <p class="text-xs text-n-amber-11">
                <span class="font-semibold">
                  {{ t('FOLLOW_UP_RULES.FORM.NOTICE_TITLE') }}:
                </span>
                {{ t('FOLLOW_UP_RULES.FORM.NOTICE_TEXT') }}
              </p>
            </div>

            <!-- Form Actions -->
            <div class="flex gap-3 mt-4 pt-4 border-t border-n-weak">
              <NextButton
                variant="faded"
                color="slate"
                class="flex-1"
                @click="showForm = false"
              >
                {{ t('FOLLOW_UP_RULES.FORM.CANCEL') }}
              </NextButton>
              <NextButton
                variant="solid"
                color="blue"
                class="flex-1"
                :is-loading="isSaving"
                @click="handleSubmit"
              >
                {{
                  editingRule
                    ? t('FOLLOW_UP_RULES.FORM.UPDATE')
                    : t('FOLLOW_UP_RULES.FORM.CREATE')
                }}
              </NextButton>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="rules.length === 0" class="text-center py-8">
          <div
            class="w-12 h-12 rounded-full bg-n-alpha-3 flex items-center justify-center mx-auto mb-3"
          >
            <span class="i-lucide-zap size-6 text-n-slate-10" />
          </div>
          <h4 class="text-sm font-medium text-n-slate-12 mb-1">
            {{ t('FOLLOW_UP_RULES.EMPTY.TITLE') }}
          </h4>
          <p class="text-sm text-n-slate-11 max-w-sm mx-auto mb-4">
            {{ t('FOLLOW_UP_RULES.EMPTY.DESCRIPTION') }}
          </p>
          <NextButton
            variant="solid"
            color="blue"
            size="sm"
            icon="add"
            @click="openCreateForm"
          >
            {{ t('FOLLOW_UP_RULES.EMPTY.CREATE_FIRST') }}
          </NextButton>
        </div>

        <!-- Rules List -->
        <div v-else class="space-y-3">
          <!-- Add Rule Button -->
          <div class="flex justify-end mb-1">
            <NextButton
              variant="faded"
              color="blue"
              size="sm"
              icon="add"
              @click="openCreateForm"
            >
              {{ t('FOLLOW_UP_RULES.ADD_RULE') }}
            </NextButton>
          </div>

          <div
            v-for="rule in rules"
            :key="rule.id"
            class="p-4 border border-n-weak rounded-lg hover:border-n-slate-7 transition-colors"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-2">
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full"
                    :class="[
                      rule.enabled
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-alpha-3 text-n-slate-11',
                    ]"
                  >
                    {{
                      rule.enabled
                        ? t('FOLLOW_UP_RULES.ACTIVE')
                        : t('FOLLOW_UP_RULES.INACTIVE')
                    }}
                  </span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ rule.follow_up_template_name }}
                  </span>
                </div>

                <div class="flex items-center gap-2 text-sm text-n-slate-11">
                  <span
                    v-if="rule.from_stage"
                    class="px-2 py-0.5 bg-n-alpha-3 rounded text-xs"
                  >
                    {{ getStageName(rule.from_stage) }}
                  </span>
                  <span v-else class="text-xs italic">{{
                    t('FOLLOW_UP_RULES.ANY_STAGE')
                  }}</span>
                  <span class="i-lucide-arrow-right size-4" />
                  <span
                    class="px-2 py-0.5 bg-n-blue-3 text-n-blue-11 rounded text-xs font-medium"
                  >
                    {{ getStageName(rule.to_stage) }}
                  </span>
                </div>

                <p class="text-xs text-n-slate-10 mt-2">
                  {{ t('FOLLOW_UP_RULES.DELAY_LABEL') }}:
                  {{ getDelayDescription(rule) }}
                </p>
                <SendWindowBadge
                  v-if="rule.send_window && rule.send_window.enabled"
                  :window="rule.send_window"
                  class="mt-1.5"
                />
              </div>

              <div class="flex items-center gap-2">
                <button
                  class="p-1.5 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                  :title="t('FOLLOW_UP_RULES.TOGGLE')"
                  @click="toggleEnabled(rule)"
                >
                  <span
                    :class="
                      rule.enabled
                        ? 'i-lucide-toggle-right text-n-teal-9'
                        : 'i-lucide-toggle-left'
                    "
                    class="size-5"
                  />
                </button>
                <button
                  class="p-1.5 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                  :title="t('FOLLOW_UP_RULES.EDIT')"
                  @click="openEditForm(rule)"
                >
                  <span class="i-lucide-pencil size-4" />
                </button>
                <button
                  class="p-1.5 text-n-slate-10 hover:text-n-ruby-9 transition-colors"
                  :title="t('FOLLOW_UP_RULES.DELETE')"
                  @click="handleDelete(rule)"
                >
                  <span class="i-lucide-trash-2 size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
/* Ensure native select arrow is hidden across all browsers */
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none;
}

/* For IE */
select::-ms-expand {
  display: none;
}
</style>
